import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Bug, Plus, X, Loader2, Search, AlertTriangle, Clock,
  CheckCircle2, ChevronRight, RefreshCw, Image as ImageIcon, ExternalLink, Trash2,
  Repeat, MessageCircle, Ban,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_AREAS = [
  "Referral Intake", "CRM Queue", "Reports", "Activity Reports",
  "User Management", "Audit Log", "Login", "Other",
];
const PRIORITIES = [
  { value: "low",      label: "Low",      ring: "bg-slate-100 text-slate-700" },
  { value: "medium",   label: "Medium",   ring: "bg-blue-100 text-blue-700"   },
  { value: "high",     label: "High",     ring: "bg-amber-100 text-amber-700" },
  { value: "critical", label: "Critical", ring: "bg-rose-100 text-rose-700"   },
];
const PRIORITY_LABEL = Object.fromEntries(PRIORITIES.map((p) => [p.value, p.label]));
const PRIORITY_RING  = Object.fromEntries(PRIORITIES.map((p) => [p.value, p.ring]));
const PRIORITY_RANK  = Object.fromEntries(PRIORITIES.map((p, i) => [p.value, i]));  // low→0, critical→3

const STATUSES = [
  { value: "open",              label: "Open",              ring: "bg-rose-100 text-rose-700",     icon: AlertTriangle },
  { value: "in_progress",       label: "In Progress",       ring: "bg-amber-100 text-amber-700",   icon: Clock },
  { value: "awaiting_retest",   label: "Awaiting Re-test",  ring: "bg-blue-100 text-blue-700",     icon: Repeat },
  { value: "awaiting_feedback", label: "Awaiting Feedback", ring: "bg-yellow-100 text-yellow-800", icon: MessageCircle },
  { value: "raintree_blocked",  label: "Raintree Blocked",  ring: "bg-orange-100 text-orange-800", icon: Ban },
  { value: "resolved",          label: "Resolved",          ring: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.value, s.label]));
const STATUS_RING  = Object.fromEntries(STATUSES.map((s) => [s.value, s.ring]));
const STATUS_ICON  = Object.fromEntries(STATUSES.map((s) => [s.value, s.icon]));

const getStoredToken = () =>
  sessionStorage.getItem("token") || localStorage.getItem("token");

// Project-wide error formatter (mirrors CRMQueue / ReferralIntake helpers)
function formatApiError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : (d?.msg || JSON.stringify(d))))
      .join("; ");
  }
  if (typeof detail === "object") return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// ── Pretty pill components ───────────────────────────────────────────────────
function PriorityBadge({ value }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_RING[value] || "bg-slate-100 text-slate-700"}`}>
      {PRIORITY_LABEL[value] || value}
    </span>
  );
}
function StatusBadge({ value }) {
  const Icon = STATUS_ICON[value] || Bug;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_RING[value] || "bg-slate-100 text-slate-700"}`}>
      <Icon size={12} />
      {STATUS_LABEL[value] || value}
    </span>
  );
}

// ── Report Bug modal ────────────────────────────────────────────────────────
function ReportBugModal({ open, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [showUrlField, setShowUrlField] = useState(false);
  // List of {serverUrl, localPreview} pairs. Local blob URLs render the
  // thumbnails without auth headers; serverUrls are what we POST.
  const [shots, setShots] = useState([]);
  const fileInputRef = React.useRef(null);
  const [form, setForm] = useState({
    title: "", description: "", page_area: "CRM Queue", priority: "medium",
  });

  // Cleanup any blob URLs we own when the component unmounts
  useEffect(() => {
    return () => {
      shots.forEach((s) => s.localPreview && URL.revokeObjectURL(s.localPreview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) {
      setBusy(false); setUploading(false); setError(""); setShowUrlField(false);
      setForm({ title: "", description: "", page_area: "CRM Queue", priority: "medium" });
      setShots((prev) => {
        prev.forEach((s) => s.localPreview && URL.revokeObjectURL(s.localPreview));
        return [];
      });
    }
  }, [open]);

  // Upload an image file; appends to the shots list rather than replacing.
  const uploadImage = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      setError("Only image files can be attached.");
      return;
    }
    setUploading(true); setError("");
    const localPreview = URL.createObjectURL(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/bugs/upload-screenshot`, fd, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      setShots((prev) => [...prev, { serverUrl: res.data.url, localPreview }]);
    } catch (err) {
      URL.revokeObjectURL(localPreview);
      setError(formatApiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  }, []);

  // Paste an image from clipboard anywhere inside the modal
  const onPaste = useCallback((e) => {
    if (!open) return;
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          uploadImage(file);
          return;
        }
      }
    }
  }, [open, uploadImage]);

  // Drag and drop — accept multiple files at once
  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    for (const f of files) {
      // Sequential so the order is preserved in the UI
      // eslint-disable-next-line no-await-in-loop
      await uploadImage(f);
    }
  }, [uploadImage]);

  // Bind a global paste listener while the modal is open
  useEffect(() => {
    if (!open) return;
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [open, onPaste]);

  const removeShot = (idx) => {
    setShots((prev) => {
      const removed = prev[idx];
      if (removed?.localPreview) URL.revokeObjectURL(removed.localPreview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const onFilesChosen = async (files) => {
    for (const f of Array.from(files || [])) {
      // eslint-disable-next-line no-await-in-loop
      await uploadImage(f);
    }
  };

  if (!open) return null;

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        screenshot_urls: shots.map((s) => s.serverUrl),
      };
      const res = await axios.post(`${API}/api/bugs`, payload, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      onCreated?.(res.data);
      onClose();
    } catch (err) {
      setError(formatApiError(err, "Failed to create bug"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="report-bug-modal"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bug size={18} className="text-rose-600" />
            <h3 className="font-bold text-slate-900">Report a Bug</h3>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 hover:bg-slate-100 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Title<span className="text-rose-500">*</span></label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Short summary of the issue"
              maxLength={200}
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm"
              data-testid="bug-title-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Description<span className="text-rose-500">*</span></label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Steps to reproduce, expected vs actual, browser/device…"
              rows={5}
              className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none"
              data-testid="bug-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Page / Area</label>
              <select
                value={form.page_area}
                onChange={(e) => setForm({ ...form, page_area: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="bug-page-area-select"
              >
                {PAGE_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
                data-testid="bug-priority-select"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          {/* Screenshots — multi-paste / drop / browse */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Screenshots <span className="text-slate-400">(optional, multiple allowed)</span>
            </label>

            {shots.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2" data-testid="bug-screenshots-grid">
                {shots.map((s, i) => (
                  <div key={i} className="relative border border-slate-200 rounded-lg overflow-hidden bg-slate-50 aspect-video">
                    <img src={s.localPreview} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeShot(i)}
                      className="absolute top-1 right-1 p-1 bg-slate-900/70 hover:bg-slate-900 text-white rounded-full"
                      title="Remove"
                      data-testid={`bug-screenshot-remove-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-4 border-2 border-dashed border-slate-200 rounded-lg text-center bg-slate-50 hover:bg-slate-100 hover:border-slate-300 transition-colors disabled:opacity-50"
              data-testid="bug-screenshot-dropzone"
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 size={16} className="animate-spin" />
                  Uploading…
                </div>
              ) : (
                <>
                  <ImageIcon size={18} className="mx-auto text-slate-400 mb-1" />
                  <p className="text-sm text-slate-700 font-medium">
                    {shots.length === 0 ? "Paste, drop, or click to attach" : "Add another screenshot"}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    PNG · JPG · GIF · WEBP &middot; max 5 MB each
                  </p>
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              onChange={(e) => onFilesChosen(e.target.files)}
              className="hidden"
              data-testid="bug-screenshot-file-input"
            />

            {/* Optional fallback for external URLs (OneDrive links etc.) */}
            <div className="mt-2">
              {!showUrlField ? (
                <button
                  type="button"
                  onClick={() => setShowUrlField(true)}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="bug-screenshot-url-toggle"
                >
                  Or paste a link instead
                </button>
              ) : (
                <input
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                      const url = e.currentTarget.value.trim();
                      e.currentTarget.value = "";
                      // Treat external URLs as already-uploaded — store them with a fake localPreview
                      // (we can't render them as an <img> reliably either, so just show a stub).
                      setShots((prev) => [...prev, { serverUrl: url, localPreview: url }]);
                    }
                  }}
                  placeholder="Paste link and press Enter…"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm"
                  data-testid="bug-screenshot-input"
                />
              )}
            </div>
          </div>

          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{error}</div>}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-lg disabled:opacity-50">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || uploading || !form.title.trim() || !form.description.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
            data-testid="bug-submit-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bug size={14} />}
            Submit Bug
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Auth-aware screenshot viewer ────────────────────────────────────────────
// Internal screenshots live behind /api/bugs/screenshots/{name} which requires
// auth headers, so we fetch the image as a blob and render the resulting
// object URL. External http(s) URLs are rendered with a plain <img>.
function BugScreenshotImg({ url, className }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    setErr(""); setSrc(null);
    if (!url) return;

    if (url.startsWith("http")) { setSrc(url); return; }

    (async () => {
      try {
        // Use fetch() instead of axios+XHR — avoids responseText/blob clash
        // that some dev-tool error reporters trigger and which surfaces as
        // an opaque "Script error" overlay even though the request succeeded.
        const resp = await fetch(`${API}${url}`, {
          headers: { Authorization: `Bearer ${getStoredToken()}` },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        setSrc(objUrl);
      } catch (e) {
        if (!cancelled) setErr("Failed to load");
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [url]);

  if (err) {
    return <div className={`${className} flex items-center justify-center text-xs text-rose-600 bg-rose-50`}>!</div>;
  }
  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center bg-slate-50 text-slate-400`}>
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }
  return <img src={src} alt="screenshot" className={className} />;
}

// Gallery in the detail panel — supports multiple screenshots, click to open full,
// admin-only "remove" overlay, and an "add more" dropzone.
function BugScreenshotsGallery({ bug, isAdmin, onUpdate }) {
  const urls = bug.screenshot_urls && bug.screenshot_urls.length
    ? bug.screenshot_urls
    : (bug.screenshot_url ? [bug.screenshot_url] : []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = React.useRef(null);

  const openFull = async (url) => {
    if (url.startsWith("http")) { window.open(url, "_blank", "noopener,noreferrer"); return; }
    try {
      const resp = await fetch(`${API}${url}`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const obj = URL.createObjectURL(blob);
      window.open(obj, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the new tab has time to load it
      setTimeout(() => URL.revokeObjectURL(obj), 60_000);
    } catch (e) {
      setError("Failed to open");
    }
  };

  const remove = async (url) => {
    setError("");
    try {
      const res = await axios.delete(`${API}/api/bugs/${bug.id}/screenshots`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
        data: { url },
      });
      onUpdate?.(res.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to remove"));
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { setError("Only image files allowed."); return; }
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/api/bugs/upload-screenshot`, fd, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      const res = await axios.post(`${API}/api/bugs/${bug.id}/screenshots`,
        { url: up.data.url },
        { headers: { Authorization: `Bearer ${getStoredToken()}` } },
      );
      onUpdate?.(res.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to add screenshot"));
    } finally {
      setUploading(false);
    }
  };

  // Paste images directly into the detail panel
  const onPaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const f = it.getAsFile();
        if (f) { e.preventDefault(); upload(f); return; }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bug.id]);

  // Drag and drop onto the detail panel
  const onDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await upload(f);
    }
  };

  return (
    <div data-testid="bug-screenshot-viewer" onPaste={onPaste} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
          Screenshots {urls.length > 0 && <span className="text-slate-400 normal-case">· {urls.length}</span>}
        </p>
      </div>

      {urls.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {urls.map((u, i) => (
            <div
              key={u}
              className="relative aspect-video border border-slate-200 rounded-lg overflow-hidden bg-slate-50 group cursor-pointer"
              onClick={() => openFull(u)}
              data-testid={`bug-screenshot-${i}`}
            >
              <BugScreenshotImg url={u} className="w-full h-full object-cover" />
              {isAdmin && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(u); }}
                  className="absolute top-1 right-1 p-1 bg-slate-900/80 hover:bg-rose-600 text-white rounded-full shadow transition-colors"
                  title="Remove screenshot (admin only)"
                  data-testid={`bug-screenshot-remove-${i}`}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Anyone authenticated can add a screenshot — encourages collaborators
          who reproduce the bug to attach evidence. */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full px-3 py-2.5 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50 hover:bg-slate-100 hover:border-slate-300 text-xs text-slate-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="bug-add-screenshot-btn"
      >
        {uploading ? (
          <><Loader2 size={14} className="animate-spin" />Uploading…</>
        ) : (
          <><ImageIcon size={14} />{urls.length === 0 ? "Paste, drop, or click to attach a screenshot" : "Add another screenshot"}</>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        multiple
        onChange={async (e) => { for (const f of Array.from(e.target.files || [])) { /* eslint-disable-next-line no-await-in-loop */ await upload(f); } e.target.value = ""; }}
        className="hidden"
        data-testid="bug-add-screenshot-input"
      />

      {error && <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 mt-2">{error}</p>}
    </div>
  );
}

// ── Detail panel (right side) ───────────────────────────────────────────────
function BugDetail({ bug, isAdmin, onUpdate, onDeleted, onClose }) {
  const [draft, setDraft] = useState({
    status: bug.status, priority: bug.priority,
    assigned_to: bug.assigned_to || "", resolution_notes: bug.resolution_notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setDraft({
      status: bug.status, priority: bug.priority,
      assigned_to: bug.assigned_to || "", resolution_notes: bug.resolution_notes || "",
    });
    setError("");
  }, [bug.id, bug.status, bug.priority, bug.assigned_to, bug.resolution_notes]);

  const dirty =
    draft.status !== bug.status ||
    draft.priority !== bug.priority ||
    (draft.assigned_to || "") !== (bug.assigned_to || "") ||
    (draft.resolution_notes || "") !== (bug.resolution_notes || "");

  const save = async () => {
    if (!isAdmin) return;
    if (draft.status === "resolved" && !draft.resolution_notes.trim()) {
      setError("Please add resolution notes when marking a bug resolved.");
      return;
    }
    setBusy(true); setError("");
    try {
      const res = await axios.patch(`${API}/api/bugs/${bug.id}`, draft, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      onUpdate?.(res.data);
    } catch (err) {
      setError(formatApiError(err, "Failed to update bug"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isAdmin) return;
    const ok = window.confirm(
      `Delete bug #${bug.id} "${bug.title}"?\n\nThis cannot be undone. ` +
      `For real bugs you usually want to mark them Resolved instead.`
    );
    if (!ok) return;
    setDeleting(true); setError("");
    try {
      await axios.delete(`${API}/api/bugs/${bug.id}`, {
        headers: { Authorization: `Bearer ${getStoredToken()}` },
      });
      onDeleted?.(bug.id);
    } catch (err) {
      setError(formatApiError(err, "Failed to delete bug"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-lg overflow-hidden" data-testid="bug-detail-panel">
      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="min-w-0">
          <h3 className="font-bold text-slate-900 truncate">#{bug.id} · {bug.title}</h3>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge value={bug.status} />
            <PriorityBadge value={bug.priority} />
            <span className="text-xs text-slate-500">{bug.page_area}</span>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg" data-testid="bug-detail-close">
          <X size={18} />
        </button>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Description</p>
          <p className="text-sm text-slate-800 whitespace-pre-wrap">{bug.description}</p>
        </div>

        {/* Screenshots gallery — shows all attached images, supports add/remove */}
        <BugScreenshotsGallery bug={bug} isAdmin={isAdmin} onUpdate={onUpdate} />

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Submitted by</p>
            <p className="text-slate-800">{bug.submitted_by}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Created</p>
            <p className="text-slate-800">{fmtDate(bug.created_at)}</p>
          </div>
          {bug.resolved_at && (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Resolved</p>
              <p className="text-slate-800">{fmtDate(bug.resolved_at)}</p>
            </div>
          )}
        </div>

        {/* Admin-only edit area */}
        <div className="border-t border-slate-200 pt-5">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
            {isAdmin ? "Triage" : "Triage (admin only)"}
          </p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
              <select
                value={draft.status}
                disabled={!isAdmin || busy}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
                data-testid="bug-edit-status"
              >
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Priority</label>
              <select
                value={draft.priority}
                disabled={!isAdmin || busy}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
                data-testid="bug-edit-priority"
              >
                {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">Assigned to</label>
            <input
              value={draft.assigned_to}
              disabled={!isAdmin || busy}
              onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })}
              placeholder="username"
              className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="bug-edit-assigned"
            />
          </div>

          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Resolution notes
              {draft.status === "resolved" && <span className="text-rose-500"> *</span>}
            </label>
            <textarea
              value={draft.resolution_notes}
              disabled={!isAdmin || busy}
              onChange={(e) => setDraft({ ...draft, resolution_notes: e.target.value })}
              placeholder={draft.status === "resolved" ? "Required when resolving — what was the fix?" : "How was this fixed? (filled when resolving)"}
              rows={3}
              className="w-full p-3 rounded-lg border border-slate-200 text-sm resize-none disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="bug-edit-resolution"
            />
          </div>

          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3 mb-3">{error}</div>}

          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={save}
                disabled={!dirty || busy || deleting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bug-save-btn"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Save changes
              </button>
              <button
                onClick={remove}
                disabled={deleting || busy}
                title="Delete this bug (admin only — prefer Resolved for real bugs)"
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg disabled:opacity-50"
                data-testid="bug-delete-btn"
              >
                {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function BugTracker() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [bugs, setBugs]   = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("open");        // open | in_progress | resolved | all
  const [search, setSearch]   = useState("");
  const [sort, setSort]       = useState("date_desc");   // date_desc | date_asc | priority
  const [selectedId, setSelectedId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const fetchData = useCallback(async () => {
    const token = getStoredToken();
    if (!token) return;
    setLoading(true);
    try {
      const [bugsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/bugs`, {
          params: { status_filter: filter },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/api/bugs/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setBugs(bugsRes.data.bugs || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to fetch bugs:", formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Counts for the filter pills come from /stats — independent of `filter`,
  // mirroring the Referral Intake fix.
  const openCount       = stats?.open               ?? 0;
  const progressCount   = stats?.in_progress        ?? 0;
  const retestCount     = stats?.awaiting_retest    ?? 0;
  const feedbackCount   = stats?.awaiting_feedback  ?? 0;
  const blockedCount    = stats?.raintree_blocked   ?? 0;
  const resolvedCount   = stats?.resolved           ?? 0;
  const totalCount      = stats?.total              ?? 0;

  const filteredAndSorted = useMemo(() => {
    let xs = bugs;
    if (search) {
      const q = search.toLowerCase();
      xs = xs.filter((b) =>
        b.title?.toLowerCase().includes(q) ||
        b.description?.toLowerCase().includes(q) ||
        b.submitted_by?.toLowerCase().includes(q) ||
        b.assigned_to?.toLowerCase().includes(q)
      );
    }
    if (sort === "date_desc")      xs = [...xs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sort === "date_asc")  xs = [...xs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sort === "priority")  xs = [...xs].sort((a, b) => (PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority]) || (new Date(b.created_at) - new Date(a.created_at)));
    return xs;
  }, [bugs, search, sort]);

  const selected = bugs.find((b) => b.id === selectedId) || null;

  return (
    <div className="p-6 space-y-5" data-testid="bug-tracker-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bug size={24} className="text-rose-600" />
            Bug Tracker
          </h1>
          <p className="text-sm text-slate-500">Internal tool to report and triage portal issues</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="bug-refresh-btn"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg"
            data-testid="report-bug-btn"
          >
            <Plus size={16} />
            Report Bug
          </button>
        </div>
      </div>

      {/* Stats bar — 4 cards: Open, In Progress, Waiting (retest+feedback+blocked), Resolved */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { key: "open",        label: "Open",        n: openCount,     ring: "border-rose-200 text-rose-700"     },
          { key: "in_progress", label: "In Progress", n: progressCount, ring: "border-amber-200 text-amber-700"   },
          { key: "waiting",     label: "Waiting",     n: retestCount + feedbackCount + blockedCount, ring: "border-blue-200 text-blue-700" },
          { key: "resolved",    label: "Resolved",    n: resolvedCount, ring: "border-emerald-200 text-emerald-700" },
        ].map((s) => (
          <div key={s.key} className={`bg-white rounded-xl border ${s.ring} p-4`} data-testid={`bug-stat-${s.key}`}>
            <p className="text-xs uppercase tracking-wide opacity-70">{s.label}</p>
            <p className="text-2xl font-bold mt-1">{s.n}</p>
          </div>
        ))}
      </div>

      {/* Filter pills + search + sort */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex flex-wrap items-center gap-1 bg-slate-100 p-1 rounded-lg">
          {[
            { key: "open",              label: `Open (${openCount})`                       },
            { key: "in_progress",       label: `In Progress (${progressCount})`            },
            { key: "awaiting_retest",   label: `Awaiting Re-test (${retestCount})`         },
            { key: "awaiting_feedback", label: `Awaiting Feedback (${feedbackCount})`      },
            { key: "raintree_blocked",  label: `Raintree Blocked (${blockedCount})`        },
            { key: "resolved",          label: `Resolved (${resolvedCount})`               },
            { key: "all",               label: `All (${totalCount})`                       },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === f.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`bug-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bugs…"
              className="pl-8 pr-3 h-9 rounded-lg border border-slate-200 text-sm w-56"
              data-testid="bug-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white"
            data-testid="bug-sort"
          >
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="priority">By priority</option>
          </select>
        </div>
      </div>

      {/* List + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={selected ? "lg:col-span-2" : "lg:col-span-3"}>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Title</th>
                  <th className="text-left px-4 py-2 font-medium">Page / Area</th>
                  <th className="text-left px-4 py-2 font-medium">Priority</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Submitted by</th>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    <Loader2 size={18} className="animate-spin inline mr-2" />Loading…
                  </td></tr>
                )}
                {!loading && filteredAndSorted.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="bug-empty-state">
                    No bugs match this filter.
                  </td></tr>
                )}
                {!loading && filteredAndSorted.map((b) => (
                  <tr
                    key={b.id}
                    onClick={() => setSelectedId(b.id)}
                    className={`cursor-pointer transition-colors ${selectedId === b.id ? "bg-blue-50" : "hover:bg-slate-50"}`}
                    data-testid={`bug-row-${b.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 truncate max-w-xs">{b.title}</p>
                      <p className="text-xs text-slate-500 truncate max-w-xs">{b.description}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{b.page_area}</td>
                    <td className="px-4 py-3"><PriorityBadge value={b.priority} /></td>
                    <td className="px-4 py-3"><StatusBadge value={b.status} /></td>
                    <td className="px-4 py-3 text-slate-700">{b.submitted_by}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                    <td className="px-2 text-slate-400">
                      <ChevronRight size={16} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selected && (
          <div className="lg:col-span-1">
            <BugDetail
              bug={selected}
              isAdmin={isAdmin}
              onClose={() => setSelectedId(null)}
              onUpdate={(updated) => {
                setBugs((xs) => xs.map((x) => (x.id === updated.id ? updated : x)));
                fetchData();  // refresh stats so pill counts stay accurate
              }}
              onDeleted={(deletedId) => {
                setBugs((xs) => xs.filter((x) => x.id !== deletedId));
                setSelectedId(null);
                fetchData();  // refresh stats
              }}
            />
          </div>
        )}
      </div>

      <ReportBugModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        onCreated={() => fetchData()}
      />
    </div>
  );
}
